import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getDeviceFingerprint } from '../utils/fingerprint';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  ExpressCheckoutElement,
  AddressElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import axios from 'axios';
import { getAuthToken, getAuthConfig, isAuthenticated, getUser, getCurrentService } from '../utils/auth';
import { getStoredAttribution } from '../utils/attribution';
import AppNav from '../components/AppNav';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_key_here');

function CheckoutForm({ plan, planSlug, customerInfo, onCustomerInfoChange, isTrialing, billingInterval, actionType, savedBillingProfile, intentId, onDiscountChange }) {
  const stripe = useStripe();
  const API_URL_CF = process.env.REACT_APP_API_URL || window.location.origin;
  const notifyTrialCardFailed = async (errorMessage) => {
    if (!isTrialing || !intentId) return;
    try {
      const { getAuthToken: _gat } = await import('../utils/auth');
      await fetch(`${API_URL_CF}/api/subscriptions/trial-card-failed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${_gat()}` },
        body: JSON.stringify({ setupIntentId: intentId, planSlug, errorMessage }),
      });
    } catch (_e) { /* non-critical */ }
  };
  const elements = useElements();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [expressCheckoutAvailable, setExpressCheckoutAvailable] = useState(false);
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoMsg, setPromoMsg] = useState('');
  const [promoValid, setPromoValid] = useState(false);
  const [promoCodeId, setPromoCodeId] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [showBusinessDetails, setShowBusinessDetails] = useState(false);
  const [businessDetails, setBusinessDetails] = useState({
    businessName: '',
    taxId: '',
    businessAddress: '',
    businessCity: '',
    businessState: '',
    businessZip: '',
    businessCountry: ''
  });

  // Pre-fill business details from saved billing profile
  useEffect(() => {
    if (savedBillingProfile) {
      setBusinessDetails({
        businessName: savedBillingProfile.business_name || '',
        taxId: savedBillingProfile.tax_id || '',
        businessAddress: savedBillingProfile.business_address || '',
        businessCity: savedBillingProfile.business_city || '',
        businessState: savedBillingProfile.business_state || '',
        businessZip: savedBillingProfile.business_zip || '',
        businessCountry: savedBillingProfile.business_country || ''
      });
      setShowBusinessDetails(true);
    }
  }, [savedBillingProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || !customerInfo.email || !customerInfo.name) {
      setError('Please fill in all required fields');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message);
        setProcessing(false);
        return;
      }

      // Store business details in sessionStorage for SuccessPage to pick up
      if (businessDetails.businessName || businessDetails.taxId) {
        sessionStorage.setItem('openhosst_business_details', JSON.stringify(businessDetails));
      }

      // Get auth token and encode it for URL (base64url encoding to make it URL-safe)
      const authToken = getAuthToken();
      const encodedToken = authToken ? encodeURIComponent(authToken) : '';

      // For trial subscriptions, use confirmSetup (captures payment method without charging)
      // For regular subscriptions, use confirmPayment (charges immediately)
      if (isTrialing) {
        const { error: confirmError, setupIntent: confirmedSetupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/success?token=${encodedToken}&billing=${billingInterval || 'monthly'}&plan=${planSlug || ''}`,
            payment_method_data: {
              billing_details: {
                email: customerInfo.email,
                name: customerInfo.name,
                address: customerInfo.address
              }
            }
          },
          redirect: 'if_required',
        });

        if (confirmError) {
          setError(confirmError.message);
          notifyTrialCardFailed(confirmError.message);
          setProcessing(false);
        } else if (confirmedSetupIntent) {
          navigate(`/success?token=${encodedToken}&setup_intent=${confirmedSetupIntent.id}&redirect_status=succeeded&billing=${billingInterval || 'monthly'}`, { replace: true });
        } else {
          // Neither error nor intent — Stripe redirected for 3DS; page will navigate away
          // If redirect was blocked, reset processing state
          setProcessing(false);
        }
      } else {
        const { error: confirmError, paymentIntent: confirmedPaymentIntent } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/success?token=${encodedToken}&billing=${billingInterval || 'monthly'}&plan=${planSlug || ''}`,
            receipt_email: customerInfo.email,
            payment_method_data: {
              billing_details: {
                email: customerInfo.email,
                name: customerInfo.name,
                address: customerInfo.address
              }
            }
          },
          redirect: 'if_required',
        });

        if (confirmError) {
          setError(confirmError.message);
          setProcessing(false);
        } else if (confirmedPaymentIntent) {
          navigate(`/success?token=${encodedToken}&payment_intent=${confirmedPaymentIntent.id}&redirect_status=succeeded&billing=${billingInterval || 'monthly'}`, { replace: true });
        } else {
          // Neither error nor intent — Stripe redirected for 3DS; page will navigate away
          setProcessing(false);
        }
      }
    } catch (err) {
      console.error('[checkout] Unexpected error:', err);
      setError(err?.message && !err.message.includes('stripe') ? err.message : 'An unexpected error occurred. Please try again.');
      setProcessing(false);
    }
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoMsg('');
    try {
      const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
      const { getAuthToken } = await import('../utils/auth');
      const res = await fetch(`${API_URL}/api/validate-promo-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ code: promoCode.trim() })
      });
      const data = await res.json();
      setPromoValid(data.valid);
      if (data.valid) {
        setPromoMsg(`${data.description} applied`);
        setPromoCodeId(data.promoCodeId);
        sessionStorage.setItem('openhosst_promo_code_id', data.promoCodeId);
        // Lift the applied discount up so the order summary reflects the discounted price.
        if (onDiscountChange) {
          const base = (billingInterval === 'yearly' && plan.yearlyPrice) ? plan.yearlyPrice : plan.price;
          const d = data.discount || {};
          let amountOff = 0;
          if (d.percentOff) amountOff = base * d.percentOff / 100;
          else if (d.amountOff) amountOff = Math.min(d.amountOff / 100, base);
          if (amountOff > 0) {
            onDiscountChange({
              description: data.description,
              amountOff,
              finalPrice: Math.max(0, base - amountOff),
              duration: d.duration || 'once',
              durationInMonths: d.durationInMonths || null,
            });
          } else {
            onDiscountChange(null);
          }
        }
      } else {
        setPromoMsg('Invalid or expired promo code');
        setPromoCodeId('');
        sessionStorage.removeItem('openhosst_promo_code_id');
        if (onDiscountChange) onDiscountChange(null);
      }
    } catch (err) {
      setPromoMsg('Failed to validate code');
      if (onDiscountChange) onDiscountChange(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const onExpressCheckoutReady = ({ availablePaymentMethods }) => {
    if (availablePaymentMethods && Object.keys(availablePaymentMethods).length > 0) {
      setExpressCheckoutAvailable(true);
    }
  };

  const onExpressCheckoutConfirm = async (event) => {
    if (!stripe || !elements) {
      return;
    }

    try {
      // Get auth token and encode it for URL
      const authToken = getAuthToken();
      const encodedToken = authToken ? encodeURIComponent(authToken) : '';

      // Use confirmSetup for trials, confirmPayment for regular subscriptions
      if (isTrialing) {
        const { error, setupIntent: confirmedSetupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/success?token=${encodedToken}&billing=${billingInterval || 'monthly'}&plan=${planSlug || ''}`,
            payment_method_data: {
              billing_details: {
                email: customerInfo.email,
                name: customerInfo.name,
                address: customerInfo.address
              }
            }
          },
          redirect: 'if_required'
        });

        if (error) {
          setError(error.message);
          notifyTrialCardFailed(error.message);
        } else if (confirmedSetupIntent) {
          const authToken = getAuthToken();
          const encodedToken2 = authToken ? encodeURIComponent(authToken) : '';
          navigate(`/success?token=${encodedToken2}&setup_intent=${confirmedSetupIntent.id}&redirect_status=succeeded&billing=${billingInterval || 'monthly'}`, { replace: true });
        }
      } else {
        const { error: payError, paymentIntent: confirmedPaymentIntent } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/success?token=${encodedToken}&billing=${billingInterval || 'monthly'}&plan=${planSlug || ''}`,
            payment_method_data: {
              billing_details: {
                email: customerInfo.email,
                name: customerInfo.name,
                address: customerInfo.address
              }
            }
          },
          redirect: 'if_required'
        });

        if (payError) {
          setError(payError.message);
        } else if (confirmedPaymentIntent) {
          const authToken = getAuthToken();
          const encodedToken2 = authToken ? encodeURIComponent(authToken) : '';
          navigate(`/success?token=${encodedToken2}&payment_intent=${confirmedPaymentIntent.id}&redirect_status=succeeded&billing=${billingInterval || 'monthly'}`, { replace: true });
        }
      }
    } catch (err) {

      setError('Payment failed. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form-new">
      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {/* Express Checkout (Google Pay, Apple Pay) */}
      <div className="express-checkout-section">
        <ExpressCheckoutElement
          onReady={onExpressCheckoutReady}
          onConfirm={onExpressCheckoutConfirm}
          options={{
            buttonType: {
              applePay: 'subscribe',
              googlePay: 'subscribe',
            },
            buttonHeight: 48,
            paymentMethods: {
              googlePay: 'auto',
              applePay: 'auto',
              link: 'never',
            },
          }}
        />
      </div>

      {/* Show divider only if express checkout is available */}
      {expressCheckoutAvailable && (
        <div className="or-divider-new">
          <span>Or pay with card</span>
        </div>
      )}

      {/* Email Field */}
      <div className="form-section">
        <label htmlFor="email" className="form-label-new">
          Email address
        </label>
        <input
          type="email"
          id="email"
          className="form-input-new"
          value={customerInfo.email}
          onChange={(e) => onCustomerInfoChange({ ...customerInfo, email: e.target.value })}
          placeholder="you@example.com"
          required
          disabled={processing}
        />
      </div>

      {/* Payment Method Section */}
      <div className="form-section">
        <h3 className="section-title">Payment method</h3>
        <p className="payment-method-description">
          Choose your preferred payment method. All options are secure and encrypted.
        </p>
        <PaymentElement
          options={{
            fields: {
              billingDetails: {
                email: 'never',
                name: 'never',
                address: 'never'
              }
            },
            defaultValues: {
              billingDetails: {
                email: customerInfo.email,
                name: customerInfo.name,
                address: customerInfo.address
              }
            }
          }}
          onChange={() => { if (error) setError(null); }}
        />
      </div>

      {/* Billing Address Section */}
      <div className="form-section">
        <h3 className="section-title">Billing address</h3>

        <AddressElement
          options={{
            mode: 'billing',
            defaultValues: {
              address: customerInfo.address || {}
            }
          }}
          onChange={(event) => {
            if (event.complete) {
              onCustomerInfoChange({
                ...customerInfo,
                address: event.value.address,
                name: event.value.name
              });
            }
          }}
        />
      </div>

      {/* Business Details (Optional) */}
      <div className="form-section">
        <button
          type="button"
          onClick={() => setShowBusinessDetails(!showBusinessDetails)}
          style={{
            background: 'none',
            border: 'none',
            color: '#40E0D0',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            padding: '0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <i className={`fa-solid fa-chevron-${showBusinessDetails ? 'down' : 'right'}`} style={{fontSize: '12px'}}></i>
          Business Details (Optional)
        </button>

        {showBusinessDetails && (
          <div style={{marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px'}}>
            <div>
              <label className="form-label-new" htmlFor="businessName">Business Name</label>
              <input
                type="text"
                id="businessName"
                className="form-input-new"
                value={businessDetails.businessName}
                onChange={(e) => setBusinessDetails(prev => ({...prev, businessName: e.target.value}))}
                placeholder="Company Name"
                disabled={processing}
              />
            </div>
            <div>
              <label className="form-label-new" htmlFor="taxId">Tax ID / VAT Number</label>
              <input
                type="text"
                id="taxId"
                className="form-input-new"
                value={businessDetails.taxId}
                onChange={(e) => setBusinessDetails(prev => ({...prev, taxId: e.target.value}))}
                placeholder="e.g., EU123456789"
                disabled={processing}
              />
            </div>
            <div>
              <label className="form-label-new" htmlFor="businessAddress">Business Address</label>
              <input
                type="text"
                id="businessAddress"
                className="form-input-new"
                value={businessDetails.businessAddress}
                onChange={(e) => setBusinessDetails(prev => ({...prev, businessAddress: e.target.value}))}
                placeholder="Street address"
                disabled={processing}
              />
            </div>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
              <div>
                <label className="form-label-new" htmlFor="businessCity">City</label>
                <input
                  type="text"
                  id="businessCity"
                  className="form-input-new"
                  value={businessDetails.businessCity}
                  onChange={(e) => setBusinessDetails(prev => ({...prev, businessCity: e.target.value}))}
                  placeholder="City"
                  disabled={processing}
                />
              </div>
              <div>
                <label className="form-label-new" htmlFor="businessState">State / Province</label>
                <input
                  type="text"
                  id="businessState"
                  className="form-input-new"
                  value={businessDetails.businessState}
                  onChange={(e) => setBusinessDetails(prev => ({...prev, businessState: e.target.value}))}
                  placeholder="State"
                  disabled={processing}
                />
              </div>
            </div>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
              <div>
                <label className="form-label-new" htmlFor="businessZip">ZIP / Postal Code</label>
                <input
                  type="text"
                  id="businessZip"
                  className="form-input-new"
                  value={businessDetails.businessZip}
                  onChange={(e) => setBusinessDetails(prev => ({...prev, businessZip: e.target.value}))}
                  placeholder="ZIP"
                  disabled={processing}
                />
              </div>
              <div>
                <label className="form-label-new" htmlFor="businessCountry">Country</label>
                <input
                  type="text"
                  id="businessCountry"
                  className="form-input-new"
                  value={businessDetails.businessCountry}
                  onChange={(e) => setBusinessDetails(prev => ({...prev, businessCountry: e.target.value}))}
                  placeholder="Country"
                  disabled={processing}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Promo Code Section */}
      <div className="form-section">
        <button
          type="button"
          onClick={() => setShowPromoInput(v => !v)}
          style={{
            background: 'none',
            border: 'none',
            color: '#40E0D0',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            padding: '0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <i className={`fa-solid fa-chevron-${showPromoInput ? 'down' : 'right'}`} style={{fontSize: '12px'}}></i>
          Have a promo code?
        </button>

        {showPromoInput && (
          <div style={{marginTop: '12px'}}>
            <div style={{display: 'flex', gap: '8px'}}>
              <input
                type="text"
                value={promoCode}
                onChange={e => { setPromoCode(e.target.value); setPromoMsg(''); setPromoValid(false); setPromoCodeId(''); sessionStorage.removeItem('openhosst_promo_code_id'); if (onDiscountChange) onDiscountChange(null); }}
                placeholder="e.g. RAMADAN25"
                disabled={processing || promoLoading}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  background: '#2d2d2d',
                  border: `1px solid ${promoValid ? '#22c55e' : '#444'}`,
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '14px',
                  textTransform: 'uppercase'
                }}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), validatePromoCode())}
              />
              <button
                type="button"
                onClick={validatePromoCode}
                disabled={processing || promoLoading || !promoCode.trim()}
                style={{
                  padding: '8px 16px',
                  background: '#2a2a2a',
                  color: '#40E0D0',
                  border: '1px solid #40E0D0',
                  borderRadius: '6px',
                  cursor: (processing || promoLoading || !promoCode.trim()) ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  opacity: (processing || promoLoading || !promoCode.trim()) ? 0.5 : 1
                }}
              >
                {promoLoading ? '...' : 'Apply'}
              </button>
            </div>
            {promoMsg && (
              <p style={{margin: '8px 0 0 0', fontSize: '13px', color: promoValid ? '#22c55e' : '#f87171'}}>
                {promoMsg}
              </p>
            )}
          </div>
        )}
      </div>

      <button
        type="submit"
        className="btn-subscribe"
        disabled={!stripe || processing}
      >
        {processing ? (
          <>
            <span className="spinner"></span>
            Processing...
          </>
        ) : isTrialing ? (
          'Start Free Trial'
        ) : actionType === 'upgrade' ? (
          'Pay & Request Upgrade'
        ) : actionType === 'downgrade' ? (
          'Confirm Downgrade'
        ) : (
          'Subscribe'
        )}
      </button>

      <p className="terms-text">
        By subscribing, you authorize OpenHosst to charge you according to the terms until you cancel.
        You also agree to the{' '}
        <a href="https://openhosst.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
        {' '}and{' '}
        <a href="https://openhosst.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
      </p>
    </form>
  );
}

function CheckoutPage() {
  const { planSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSubError, setActiveSubError] = useState(null); // { message, existingSubId }
  const [customerInfo, setCustomerInfo] = useState({
    email: '',
    name: '',
    address: {}
  });
  const [clientSecret, setClientSecret] = useState('');
  const [checkoutIntentId, setCheckoutIntentId] = useState('');
  const [customerAction, setCustomerAction] = useState('new');
  const [trialEligible, setTrialEligible] = useState(false);
  const [isTrialing, setIsTrialing] = useState(false);
  const [proratedCredit, setProratedCredit] = useState(0);
  const [zeroAmount, setZeroAmount] = useState(false);
  const [zeroAmountSubId, setZeroAmountSubId] = useState(null);
  const [zeroAmountProcessing, setZeroAmountProcessing] = useState(false);
  const [planPriceCents, setPlanPriceCents] = useState(0);
  const [balanceAppliedCents, setBalanceAppliedCents] = useState(0);
  const [savedBillingProfile, setSavedBillingProfile] = useState(null);
  // Discount applied via promo code in the checkout form — lifted up so the order summary
  // and after-trial price reflect it. { description, amountOff, finalPrice, duration, durationInMonths }
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  // Show error if returning from a failed 3DS redirect
  useEffect(() => {
    if (new URLSearchParams(location.search).get('auth_failed') === '1') {
      setError('Card authentication failed. Please try a different card or payment method.');
    }
  }, [location.search]);

  useEffect(() => {
    // Check authentication first
    if (!isAuthenticated()) {
      // Redirect to login if not authenticated
      navigate('/login');
      return;
    }

    // Get user data from auth
    const user = getUser();
    if (!user || !user.email) {
      navigate("/login");
      return;
    }

    // If a subscription was just completed, clear the flag (user may want to subscribe again)
    const subscriptionCompleted = sessionStorage.getItem('openhosst_subscription_completed');
    if (subscriptionCompleted) {
      sessionStorage.removeItem('openhosst_subscription_completed');
    }

    // Get domain data from navigation state (with sessionStorage fallback for mobile)
    let domainData = location.state;
    if (domainData && (domainData.domainChoice || domainData.action === 'renew')) {
      // Save to sessionStorage so it survives mobile page reloads
      sessionStorage.setItem('openhosst_checkout_state_' + planSlug, JSON.stringify(domainData));
    } else if (!domainData || !domainData.domainChoice) {
      // Try sessionStorage fallback (handles mobile browsers that lose location.state)
      const saved = sessionStorage.getItem('openhosst_checkout_state_' + planSlug);
      if (saved) {
        try { domainData = JSON.parse(saved); } catch(e) {}
      }
    }
    const customerAction = domainData?.action || 'new';
    const domainChoice = domainData?.domainChoice;

    // Check if domain selection was completed (except for renewals)
    if (customerAction !== 'renew' && !domainChoice) {
      // Redirect to domain selection, preserving currentSubscriptionId for downgrade flow
      navigate(`/domain/${planSlug}`, {
        state: {
          action: customerAction,
          billingInterval: domainData?.billingInterval,
          currentSubscriptionId: domainData?.currentSubscriptionId,
        }
      });
      return;
    }

    // Set customer info from authenticated user
    setCustomerInfo(prev => ({
      ...prev,
      email: user.email,
      name: user.fullName || ''
    }));
    setCustomerAction(customerAction);

    // Store domain data in window context for payment submission
    window._checkoutContext = {
      domainData: domainData
    };

    // Generate unique checkout session ID (only store this in sessionStorage for database queries)
    const checkoutSessionId = `checkout_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('checkoutSessionId', checkoutSessionId);

    fetchPlanAndCreateSubscription(checkoutSessionId, domainData);
  }, [planSlug, navigate, location.state]);

  const fetchPlanAndCreateSubscription = async (checkoutSessionId, domainData) => {
    try {
      setLoading(true);
      const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

      // Using card payment method only

      // Fetch plan details
      const planResponse = await axios.get(`${API_URL}/api/plan/${planSlug}`);
      setPlan(planResponse.data);

      // Save checkout session to database
      try {
        await axios.post(
          `${API_URL}/api/checkout-session`,
          {
            sessionId: checkoutSessionId,
            planSlug: planSlug,
            planName: planResponse.data.name,
            action: domainData?.action || 'new',
            domainChoice: domainData?.domainChoice,
            domain: domainData?.domain,
            subdomain: domainData?.subdomain,
            currentSubscriptionId: domainData?.currentSubscriptionId || null,
            ...getStoredAttribution(),
          },
          getAuthConfig()
        );

      } catch (dbError) {

        // Don't fail the checkout if database save fails
      }

      // Fetch customer status to check trial eligibility
      try {
        const customerResponse = await axios.post(`${API_URL}/api/check-customer`, {}, getAuthConfig());

        setTrialEligible(customerResponse.data.trialEligible || false);

      } catch (err) {

        // Default to not eligible if we can't check
        setTrialEligible(false);

      }

      // Fetch saved billing profile to pre-fill business details on checkout
      try {
        const profileRes = await axios.get(`${API_URL}/api/business-profile`, getAuthConfig());
        const bp = profileRes.data?.profile;
        if (bp && bp.billing_type === 'business' && bp.business_name) {
          setSavedBillingProfile(bp);
        }
      } catch (bpErr) {
        // Non-critical — checkout works without pre-filled business details
      }

      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Plan not found');
    } finally {
      setLoading(false);
    }
  };

  const createSubscription = async (emailAddress, customerName) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

      // Get billing interval from state (default to monthly for backward compatibility)
      const billingInterval = location.state?.billingInterval || 'monthly';

      // Select the correct price ID based on billing interval
      const selectedPriceId = billingInterval === 'yearly' && plan.yearlyPriceId
        ? plan.yearlyPriceId
        : plan.priceId;

      // Prepare payment initialization payload
      const domainCtx = window._checkoutContext?.domainData || location.state || {};
      const _initDomainType = domainCtx.domainChoice || 'subdomain';
      const _initDomain = _initDomainType === 'custom' ? (domainCtx.domain || '') : '';
      const _initSubdomain = _initDomainType === 'subdomain' ? (domainCtx.subdomain || '') : '';
      const payload = {
        priceId: selectedPriceId,
        planSlug: planSlug,
        customerName: customerName || '',
        // billing_change is the frontend action for same-plan interval switches; backend treats it as 'upgrade'
        actionType: (location.state?.action === 'billing_change' ? 'upgrade' : location.state?.action) || 'new',
        currentSubscriptionId: location.state?.currentSubscriptionId || null,
        domainType: _initDomainType,
        domain: _initDomain,
        subdomain: _initSubdomain,
        dnsHelp: _initDomainType === 'custom' ? (domainCtx.dnsHelp || null) : null,
        _fp: await getDeviceFingerprint().catch(() => null),
        // Promo code so the non-trial charge (PaymentIntent) is discounted, not just the sub.
        // Read from sessionStorage (set when the code is validated) — the promoCodeId state
        // lives in a different component scope; SuccessPage reads the same key.
        promoCodeId: sessionStorage.getItem('openhosst_promo_code_id') || null,
        // Email is extracted from JWT token on backend
      };

      // Initialize payment (SetupIntent or PaymentIntent) WITHOUT creating subscription
      const response = await axios.post(
        `${API_URL}/api/initialize-payment`,
        payload,
        getAuthConfig() // Include auth token in headers
      );

      if (response.data.alreadyProcessed) {
        // Already completed on a previous load — redirect to service page
        navigate('/' + (planSlug?.split('-')[0] || 'subscriptions'), { replace: true });
        return;
      }
      if (response.data.zeroAmount) {
        // Credit covers full invoice — no payment needed; show confirmation button
        setZeroAmount(true);
        setZeroAmountSubId(response.data.subscriptionId);
        if (response.data.proratedCredit > 0) {
          setProratedCredit(response.data.proratedCredit);
        }
        if (response.data.planPriceCents) {
          setPlanPriceCents(response.data.planPriceCents);
        }
        if (response.data.balanceAppliedCents) {
          setBalanceAppliedCents(response.data.balanceAppliedCents);
        }
        return;
      }
      setClientSecret(response.data.clientSecret);
      setCheckoutIntentId(response.data.intentId || '');
      setIsTrialing(response.data.isTrialing || false);
      if (response.data.proratedCredit > 0) {
        setProratedCredit(response.data.proratedCredit);
      }
      if (response.data.balanceAppliedCents > 0) {
        setBalanceAppliedCents(response.data.balanceAppliedCents);
      }
      if (response.data.planPriceCents) {
        setPlanPriceCents(response.data.planPriceCents);
      }

      // Update checkout session with intent ID in database
      const checkoutSessionId = sessionStorage.getItem('checkoutSessionId');
      if (checkoutSessionId && response.data.intentId) {
        try {
          // Determine intent type from clientSecret
          const setupIntentId = response.data.clientSecret?.startsWith('seti_') ?
            response.data.intentId : null;
          const paymentIntentId = response.data.clientSecret?.startsWith('pi_') ?
            response.data.intentId : null;

          // Update checkout session with intent IDs
          await axios.put(
            `${API_URL}/api/checkout-session/${checkoutSessionId}/intents`,
            { setupIntentId, paymentIntentId },
            getAuthConfig()
          );
        } catch (updateError) {
          // Don't fail checkout if database update fails
        }
      }
    } catch (err) {

      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Session expired. Please login again.');
        setTimeout(() => navigate('/subscriptions'), 2000);
      } else if (err.response?.data?.code === 'ACTIVE_SUBSCRIPTION_EXISTS') {
        setActiveSubError({
          message: err.response.data.error,
          existingSubId: err.response.data.existingSubscriptionId,
        });
      } else {
        setError(err.response?.data?.error || 'Failed to initialize payment');
      }
    }
  };

  // Create subscription when email is ready
  useEffect(() => {
    if (customerInfo.email && plan && !clientSecret) {
      createSubscription(customerInfo.email, customerInfo.name);
    }
  }, [customerInfo.email, plan, clientSecret]);

  if (loading) {
    return (
      <div className="checkout-container-new">
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (activeSubError) {
    return (
      <div className="checkout-container-new">
        <AppNav onLogout={() => {}} />
        <div className="error-container-full">
          <div className="error-icon" style={{ color: '#f59e0b' }}><i className="fa-solid fa-circle-exclamation"></i></div>
          <h1 className="error-title" style={{ fontSize: '22px' }}>Subscription Already Active</h1>
          <p className="error-message" style={{ maxWidth: '420px', lineHeight: '1.6' }}>{activeSubError.message}</p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '8px' }}>
            <button className="btn-primary-new" onClick={() => navigate('/subscriptions')}>
              <i className="fa-solid fa-list" style={{ marginRight: 6 }}></i>View My Subscriptions
            </button>
            <button className="btn-secondary-new" style={{ background: 'transparent', border: '1px solid #555', color: '#ccc', borderRadius: 8, padding: '12px 24px', cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate(-1)}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error && !plan) {
    return (
      <div className="checkout-container-new">
      <AppNav onLogout={() => {}} />
        <div className="error-container-full">
          <div className="error-icon"><i className="fa-solid fa-triangle-exclamation"></i></div>
          <h1 className="error-title">Plan Not Found</h1>
          <p className="error-message">{error}</p>
          <a href="https://openhosst.com" className="btn-primary-new">
            Return to OpenHosst
          </a>
        </div>
      </div>
    );
  }

  const appearance = {
    theme: 'night',
    variables: {
      colorPrimary: '#40E0D0',
      colorBackground: '#2d2d2d',
      colorText: '#ffffff',
      colorDanger: '#ff6b6b',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      spacingUnit: '4px',
      borderRadius: '8px',
    },
  };

  const options = {
    clientSecret,
    appearance,
    // NOTE: Don't specify mode - Stripe automatically detects it from client_secret
    // Setup Intent client secrets start with "seti_", Payment Intent with "pi_"
  };

  return (
    <div className="checkout-container-new">
      <AppNav onLogout={() => {}} />
      <div className="checkout-layout">
        {/* Left side - Form */}
        <div className="checkout-main">
          <h1 className="page-title">Configure your plan</h1>

          {zeroAmount ? (
            <div className="checkout-form-new">
              <div style={{ background: '#111', border: '1px solid #333', borderRadius: 10, padding: '24px', marginBottom: 16 }}>
                <h3 style={{ color: '#fff', margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Order Summary</h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', color: '#ccc', fontSize: 14 }}>
                  <span>{plan?.name || 'New Plan'} ({location.state?.billingInterval === 'yearly' ? 'yearly' : 'monthly'})</span>
                  <span>${(planPriceCents / 100).toFixed(2)}</span>
                </div>
                {proratedCredit > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', color: '#40E0D0', fontSize: 14 }}>
                    <span>Proration credit from current plan</span>
                    <span>-${(proratedCredit / 100).toFixed(2)}</span>
                  </div>
                )}
                {balanceAppliedCents > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', color: '#40E0D0', fontSize: 14 }}>
                    <span>Account credit applied</span>
                    <span>-${(balanceAppliedCents / 100).toFixed(2)}</span>
                  </div>
                )}
                <div style={{ borderTop: '1px solid #333', margin: '8px 0' }}></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', color: '#fff', fontSize: 16, fontWeight: 700 }}>
                  <span>Due today</span>
                  <span>$0.00</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#888', fontSize: 12 }}>
                  <span>Ongoing</span>
                  <span>${(planPriceCents / 100).toFixed(2)}/{location.state?.billingInterval === 'yearly' ? 'yr' : 'mo'}</span>
                </div>
              </div>

              <div style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.25)', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#10b981', fontSize: 14, fontWeight: 600 }}>
                  <i className="fa-solid fa-circle-check"></i>
                  {proratedCredit > 0 || balanceAppliedCents > 0
                    ? 'No payment required — your credit covers this invoice'
                    : 'No payment required for this subscription'}
                </div>
              </div>

              {error && <div className="alert alert-error">{error}</div>}
              <button
                onClick={async () => {
                  setZeroAmountProcessing(true);
                  setError(null);
                  try {
                    const { default: axios } = await import('axios');
                    const { getAuthToken } = await import('../utils/auth');
                    const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
                    const domainCtx = window._checkoutContext?.domainData || location.state || {};
                    const domainType = domainCtx.domainChoice || 'subdomain';
                    const domain = domainType === 'custom' ? domainCtx.domain : null;
                    const subdomain = domainType === 'subdomain' ? domainCtx.subdomain : null;
                    await axios.post(`${API_URL}/api/subscriptions/complete-zero-charge`, {
                      subscriptionId: zeroAmountSubId,
                      planSlug: planSlug,
                      priceId: (location.state?.billingInterval === 'yearly' && plan?.yearlyPriceId) ? plan.yearlyPriceId : plan?.priceId,
                      domain,
                      domainType,
                      subdomain,
                      dnsHelp: domainType === 'custom' ? (domainCtx.dnsHelp || null) : null,
                      actionType: (location.state?.action === 'billing_change' ? 'upgrade' : location.state?.action) || 'new',
                      currentSubscriptionId: location.state?.currentSubscriptionId || null,
                    }, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
                    navigate('/subscriptions', { state: { upgradeSuccess: true } });
                  } catch (err) {
                    setError(err.response?.data?.error || 'Failed to complete upgrade');
                    setZeroAmountProcessing(false);
                  }
                }}
                disabled={zeroAmountProcessing}
                className="checkout-submit-btn"
                style={{
                  width: '100%', padding: '14px',
                  background: zeroAmountProcessing ? '#20B2AA' : 'linear-gradient(135deg, #40E0D0 0%, #20B2AA 100%)',
                  color: '#1a1a1a',
                  border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 700,
                  cursor: zeroAmountProcessing ? 'not-allowed' : 'pointer',
                  opacity: zeroAmountProcessing ? 0.7 : 1,
                }}
              >
                {zeroAmountProcessing ? 'Processing...'
                  : location.state?.action === 'billing_change'
                    ? (location.state?.billingInterval === 'yearly' ? 'Switch to Yearly Billing' : 'Switch to Monthly Billing')
                  : location.state?.action === 'upgrade' ? 'Confirm Upgrade'
                  : location.state?.action === 'downgrade' ? 'Confirm Downgrade'
                  : location.state?.action === 'renew' ? 'Renew Subscription'
                  : 'Complete Subscription'}
              </button>
            </div>
          ) : clientSecret ? (
            <Elements stripe={stripePromise} options={options}>
              <CheckoutForm
                plan={plan}
                planSlug={planSlug}
                customerInfo={customerInfo}
                onCustomerInfoChange={setCustomerInfo}
                isTrialing={isTrialing}
                billingInterval={location.state?.billingInterval || 'monthly'}
                actionType={location.state?.action || 'new'}
                savedBillingProfile={savedBillingProfile}
                intentId={checkoutIntentId}
                onDiscountChange={setAppliedDiscount}
              />
            </Elements>
          ) : (
            <div className="checkout-form-new">
              {error && (
                <div className="alert alert-error">
                  {error}
                </div>
              )}
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="helper-text">Initializing payment...</p>
              </div>
            </div>
          )}
        </div>

        {/* Right side - Plan Summary */}
        <div className="checkout-sidebar">
          <div className="plan-card">
            <h2 className="plan-card-title">{plan.name}</h2>

            {plan.features && plan.features.length > 0 && (
              <div className="plan-features-section">
                <h3 className="features-title">Top features</h3>
                <ul className="plan-features-new">
                  {plan.features.map((feature, index) => (
                    <li key={index}>
                      <span className="feature-icon"><i className="fa-solid fa-check"></i></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="plan-pricing">
              {(() => {

                return null;
              })()}
              {plan.trialDays && plan.trialDays > 0 && trialEligible && (
                <div style={{
                  background: 'rgba(64, 224, 208, 0.1)',
                  border: '1px solid rgba(64, 224, 208, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px'
                }}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px'}}>
                    <i className="fa-solid fa-gift" style={{color: '#40E0D0', fontSize: '16px'}}></i>
                    <span style={{color: '#40E0D0', fontWeight: '600', fontSize: '14px'}}>
                      {plan.trialDays} Days Free Trial
                    </span>
                  </div>
                  <p style={{margin: '0', color: '#cccccc', fontSize: '12px', lineHeight: '1.5'}}>
                    Your {plan.trialDays}-day free trial starts once our team activates your service. We'll verify your card now to confirm it's valid — your card will not be charged during this step.
                  </p>
                </div>
              )}
              <div className="pricing-row">
                <span className="pricing-label">
                  {location.state?.billingInterval === 'yearly' ? 'Yearly subscription' : 'Monthly subscription'}
                </span>
                <span className="pricing-value">
                  ${location.state?.billingInterval === 'yearly' && plan.yearlyPrice
                    ? plan.yearlyPrice.toFixed(2)
                    : plan.price.toFixed(2)}
                </span>
              </div>
              {plan.trialDays && plan.trialDays > 0 && trialEligible && (
                <div className="pricing-row">
                  <span className="pricing-label">{plan.trialDays}-day free trial discount</span>
                  <span className="pricing-value" style={{color: '#40E0D0'}}>
                    -${location.state?.billingInterval === 'yearly' && plan.yearlyPrice
                      ? plan.yearlyPrice.toFixed(2)
                      : plan.price.toFixed(2)}
                  </span>
                </div>
              )}
              {proratedCredit > 0 && (
                <div className="pricing-row">
                  <span className="pricing-label" style={{color: '#40E0D0'}}>Proration credit</span>
                  <span className="pricing-value" style={{color: '#40E0D0'}}>
                    -${(proratedCredit / 100).toFixed(2)}
                  </span>
                </div>
              )}
              {balanceAppliedCents > 0 && (
                <div className="pricing-row">
                  <span className="pricing-label" style={{color: '#40E0D0'}}>Account credit</span>
                  <span className="pricing-value" style={{color: '#40E0D0'}}>
                    -${(balanceAppliedCents / 100).toFixed(2)}
                  </span>
                </div>
              )}
              {appliedDiscount && !zeroAmount && !(plan.trialDays && plan.trialDays > 0 && trialEligible) && (
                <div className="pricing-row">
                  <span className="pricing-label" style={{color: '#40E0D0'}}>Discount ({appliedDiscount.description})</span>
                  <span className="pricing-value" style={{color: '#40E0D0'}}>
                    -${appliedDiscount.amountOff.toFixed(2)}
                  </span>
                </div>
              )}
              <div className="pricing-row">
                <span className="pricing-label">Tax (0%)</span>
                <span className="pricing-value">$0.00</span>
              </div>
              <div className="pricing-divider"></div>

              <div className="pricing-row pricing-total">
                <span className="pricing-label">First invoice</span>
                <span className="pricing-value">
                  ${(() => {
                    if (zeroAmount) return '0.00';
                    if (plan.trialDays && plan.trialDays > 0 && trialEligible) return '0.00';
                    const base = (location.state?.billingInterval === 'yearly' && plan.yearlyPrice) ? plan.yearlyPrice : plan.price;
                    const discountCents = appliedDiscount ? Math.round(appliedDiscount.amountOff * 100) : 0;
                    const cents = Math.max(0, base * 100 - proratedCredit - balanceAppliedCents - discountCents);
                    return (cents / 100).toFixed(2);
                  })()}
                </span>
              </div>
              {(proratedCredit > 0 || balanceAppliedCents > 0) && (
                <div className="pricing-row" style={{marginTop: '4px'}}>
                  <span className="pricing-label" style={{color: '#888', fontSize: '12px'}}>Ongoing</span>
                  <span className="pricing-value" style={{color: '#888', fontSize: '12px'}}>
                    ${(() => {
                      const base = (location.state?.billingInterval === 'yearly' && plan.yearlyPrice) ? plan.yearlyPrice : plan.price;
                      const recurring = (appliedDiscount && (appliedDiscount.duration === 'forever' || appliedDiscount.duration === 'repeating')) ? appliedDiscount.finalPrice : base;
                      return Number(recurring).toFixed(2);
                    })()}/{location.state?.billingInterval === 'yearly' ? 'yr' : 'mo'}
                  </span>
                </div>
              )}
              {plan.trialDays && plan.trialDays > 0 && trialEligible && appliedDiscount && (
                <div className="pricing-row" style={{marginTop: '4px'}}>
                  <span className="pricing-label" style={{color: '#40E0D0'}}>Discount ({appliedDiscount.description})</span>
                  <span className="pricing-value" style={{color: '#40E0D0'}}>-${appliedDiscount.amountOff.toFixed(2)}/{location.state?.billingInterval === 'yearly' ? 'yr' : 'mo'}</span>
                </div>
              )}
              {plan.trialDays && plan.trialDays > 0 && trialEligible && (
                <p style={{
                  margin: '12px 0 0 0',
                  color: '#999',
                  fontSize: '12px',
                  textAlign: 'center',
                  lineHeight: '1.5'
                }}>
                  After your {plan.trialDays}-day trial, {(() => {
                    const yearly = location.state?.billingInterval === 'yearly' && plan.yearlyPrice;
                    const per = yearly ? 'year' : 'month';
                    const base = yearly ? plan.yearlyPrice : plan.price;
                    const fmt = (v) => `$${Number(v).toFixed(2)}`;
                    if (!appliedDiscount) return `you'll be charged ${fmt(base)}/${per}`;
                    const f = appliedDiscount.finalPrice;
                    if (appliedDiscount.duration === 'once') {
                      return `you'll be charged ${fmt(f)} for your first payment, then ${fmt(base)}/${per}`;
                    }
                    if (appliedDiscount.duration === 'repeating' && appliedDiscount.durationInMonths) {
                      const n = appliedDiscount.durationInMonths;
                      return `you'll be charged ${fmt(f)}/${per} for your first ${n} month${n > 1 ? 's' : ''}, then ${fmt(base)}/${per}`;
                    }
                    return `you'll be charged ${fmt(f)}/${per}`;
                  })()}. Trial begins at activation.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;
